import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function copyTextToClipboard(text: string) {
  let clipboardError: unknown;

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    textarea.style.top = "0";
    textarea.style.left = "0";

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (copied) return;
  }

  if (clipboardError instanceof Error) {
    throw clipboardError;
  }

  throw new Error("O navegador bloqueou a cópia automática neste contexto.");
}

/**
 * Convert a wall-clock date/time in a specific IANA timezone into an ISO-8601 UTC string.
 * Example: "2026-06-22", "08:00", "America/Sao_Paulo" -> the UTC instant that is 08:00 in that timezone.
 * Returns null if inputs are invalid or if the timezone is empty.
 */
export function dateTimeInTimezoneToISO(
  date: string, // yyyy-mm-dd
  time: string, // hh:mm
  timezone: string,
): string | null {
  if (!date || !time || !timezone) return null;
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) return null;

  const targetWallUTC = new Date(Date.UTC(year, month - 1, day, hour, minute));
  let guess = targetWallUTC.getTime();

  for (let i = 0; i < 5; i++) {
    const wallInTz = formatUTCDateInTimezone(new Date(guess), timezone);
    const diff = targetWallUTC.getTime() - new Date(`${wallInTz}Z`).getTime();
    if (Math.abs(diff) < 1000) return new Date(guess).toISOString();
    guess += diff;
  }

  return new Date(guess).toISOString();
}

function formatUTCDateInTimezone(date: Date, timezone: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
}
